/**
 * Single contract for Compliance Center standard selection (Overview, Assessments, Journey).
 * Aligns with backend getCanonicalStandardCode where possible.
 */

export type TranslateFn = (key: string, defaultText: string, options?: Record<string, string | number | undefined>) => string;

const LOWER_ALIASES: Record<string, string> = {
  all: "ALL",
  gdpr: "GDPR",
  dora: "DORA",
  nis2: "NIS2",
  nis_2: "NIS2",
  nis2_full: "NIS2",
  nis2_full_2024: "NIS2",
  cis: "CIS",
  cis_v8: "CIS",
  cis_controls: "CIS",
  cis_controls_v8: "CIS",
  ciscontrolsv8: "CIS",
  cis_controls_v_8: "CIS",
  soc2: "SOC2",
  soc_2: "SOC2",
  iso27001: "ISO27001",
  iso_27001: "ISO27001",
  isoiec27001: "ISO27001",
  iso42001: "ISO42001",
  iso_42001: "ISO42001",
  iso42001_2023: "ISO42001",
  "iso/iec_42001": "ISO42001",
  "iso/iec_42001:2023": "ISO42001",
  isoiec42001: "ISO42001",
  isoiec420012023: "ISO42001",
  pci_dss: "PCI_DSS_2025",
  pci_dss_2025: "PCI_DSS_2025",
  pcidss2025: "PCI_DSS_2025",
  pci: "PCI_DSS_2025",
  swift_cscf_2025: "SWIFT_CSCF_2025",
  swift_cscf: "SWIFT_CSCF_2025",
  swiftcscf2025: "SWIFT_CSCF_2025",
  swift: "SWIFT_CSCF_2025",
  anssi_fr: "ENS_ANSSI",
  anssifr: "ENS_ANSSI",
  anssi: "ENS_ANSSI",
  ens_anssi: "ENS_ANSSI",
  national_anssi_fr: "ENS_ANSSI",
  technical_controls: "TECH_CONTROLS",
  technicalcontrols: "TECH_CONTROLS",
  tech_controls: "TECH_CONTROLS",
  techcontrols: "TECH_CONTROLS",
  sdlc_security: "SDLC_SECURITY",
  sdlcsecurity: "SDLC_SECURITY",
  ens_es: "ENS_ES",
  national_ens_es: "ENS_ES",
  ens: "ENS_ES",
  bsi_de: "BSI_DE",
  national_bsi_de: "BSI_DE",
  ksbp_lt: "KSBP_LT",
  national_ksbp_lt: "KSBP_LT",
  nca_ee: "NCA_EE",
  national_nca_ee: "NCA_EE",
  all_standards: "ALL",
  portfolio: "ALL",
  portfolio_scope: "ALL",
};

const PORTFOLIO_SCOPE_VALUES = new Set([
  "",
  "ALL",
  "ALL_STANDARDS",
  "PORTFOLIO",
  "PORTFOLIO_SCOPE",
]);

/**
 * Compliance Center / API scope: null = portfolio (all assessed standards).
 * Unlike normalizeComplianceStandardCode, never returns 'ALL' — returns null instead.
 */
export function normalizeComplianceScope(value: unknown): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const compact = raw.replace(/\s+/g, "").replace(/-/g, "_");
  const asLower = compact.toLowerCase();
  if (PORTFOLIO_SCOPE_VALUES.has(asLower) || PORTFOLIO_SCOPE_VALUES.has(compact.toUpperCase())) {
    return null;
  }
  const norm = raw.replace(/\s*\/\s*/g, "/").replace(/\s+/g, " ").trim();
  const upperSpaced = norm.toUpperCase();
  if (upperSpaced === "ALL STANDARDS") return null;
  const code = normalizeComplianceStandardCode(value);
  if (code === "ALL") return null;
  return code;
}

export function isPortfolioComplianceScope(value: unknown): boolean {
  return normalizeComplianceScope(value) === null;
}

/** Append standardCode query param only when a specific standard is in scope. */
export function withComplianceScopeQuery(url: string, standard: unknown): string {
  const scoped = normalizeComplianceScope(standard);
  if (!scoped) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}standardCode=${encodeURIComponent(scoped)}`;
}

/** Normalize UI / localStorage / URL values to canonical selector codes. */
export function normalizeComplianceStandardCode(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "ALL";

  // Underscore form first (handles "Technical Controls" → technical_controls via space→_)
  const underscored = raw
    .replace(/\s*\/\s*/g, "/")
    .replace(/-/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .toLowerCase();
  if (underscored === "all") return "ALL";
  if (LOWER_ALIASES[underscored]) return LOWER_ALIASES[underscored];

  const compact = raw.replace(/\s+/g, "").replace(/-/g, "_").replace(/_/g, "");
  const asLower = compact.toLowerCase();
  if (asLower === "all") return "ALL";
  if (LOWER_ALIASES[asLower]) return LOWER_ALIASES[asLower];

  const norm = raw
    .replace(/\s*\/\s*/g, "/")
    .replace(/\s+/g, " ")
    .trim();
  const upperSpaced = norm.toUpperCase();
  if (upperSpaced === "ISO 27001" || upperSpaced === "ISO/IEC 27001") return "ISO27001";
  if (upperSpaced === "SOC 2" || upperSpaced === "SOC2") return "SOC2";
  if (upperSpaced === "PCI DSS 2025" || upperSpaced === "PCI DSS") return "PCI_DSS_2025";
  if (upperSpaced === "SWIFT CSCF 2025" || upperSpaced === "SWIFT CSCF") return "SWIFT_CSCF_2025";
  if (upperSpaced === "SDLC SECURITY") return "SDLC_SECURITY";
  if (upperSpaced === "TECHNICAL CONTROLS" || upperSpaced === "TECH CONTROLS") return "TECH_CONTROLS";
  if (upperSpaced === "NIS 2" || upperSpaced === "NIS2" || upperSpaced === "NIS2 FULL") return "NIS2";
  if (upperSpaced === "CIS CONTROLS V8" || upperSpaced.startsWith("CIS CONTROLS")) return "CIS";
  if (asLower === "nis2_full" || asLower === "nis2_full_2024") return "NIS2";
  if (upperSpaced === "ANSSI_FR" || upperSpaced === "ANSSI FR") return "ENS_ANSSI";
  if (upperSpaced === "ENS" || upperSpaced === "ENS_ES" || upperSpaced === "NATIONAL_ENS_ES") return "ENS_ES";
  if (upperSpaced === "BSI_DE" || upperSpaced === "NATIONAL_BSI_DE") return "BSI_DE";
  if (upperSpaced === "KSBP_LT" || upperSpaced === "NATIONAL_KSBP_LT") return "KSBP_LT";
  if (upperSpaced === "NCA_EE" || upperSpaced === "NATIONAL_NCA_EE") return "NCA_EE";

  // Prefer underscore canonical form over compacted mangled codes
  const underscoredUpper = underscored.toUpperCase();
  return underscoredUpper || "ALL";
}

/** Localized short label for UI (signals, modals). */
export function getStandardDisplayName(t: TranslateFn, code: string): string {
  const c = normalizeComplianceStandardCode(code);
  if (c === "ALL") return t("complianceCenterUi.standards.displayNames.ALL", "ALL standards");
  return t(`complianceCenterUi.standards.displayNames.${c}`, c);
}

/** Compare two standard codes after normalization (handles PCI_DSS vs PCI_DSS_2025, etc.). */
export function standardsEquivalent(a: unknown, b: unknown): boolean {
  return normalizeComplianceStandardCode(a) === normalizeComplianceStandardCode(b);
}

/** Persist Compliance Center scope and notify listeners (overview, journey, remediation, etc.). */
export function setComplianceCenterSelectedStandard(value: unknown): string {
  const next = normalizeComplianceStandardCode(value);
  localStorage.setItem("ccSelectedStandard", next === "ALL" ? "ALL" : next);
  window.dispatchEvent(new CustomEvent("cc:selected-standard", { detail: next }));
  return next;
}
